import styled from '@emotion/styled';
import { Box, Avatar, TextField } from '@mui/material';
import { CustomTheme } from '../../../../context/PermissionsContext/ThemeContext/themesTypes';

export const ViewContainer = styled.div<{ theme?: CustomTheme }>`
  margin-top: 0;
  padding: 24px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

export const TopThreeContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 24px;
  padding: 0 20px;
  margin-top: 8px;
  align-items: flex-end;
`;

export const TopThreeCard = styled.div<{ isLarge?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: ${({ isLarge }) => (isLarge ? '180px' : '120px')};
`;

export const TopThreeAvatar = styled.div<{
  isLarge?: boolean;
  theme?: CustomTheme;
}>`
  width: ${({ isLarge }) => (isLarge ? '120px' : '80px')};
  height: ${({ isLarge }) => (isLarge ? '120px' : '80px')};
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 8px;
  background-color: ${({ theme }) => theme.custom.background3};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const TopThreePosition = styled.div<{ theme?: CustomTheme }>`
  font-weight: 700;
  font-size: 14px;
  color: ${({ theme }) => theme.custom.primary};
`;

export const TopThreeName = styled.div<{ theme?: CustomTheme }>`
  font-weight: 600;
  font-size: 15px;
  color: ${({ theme }) => theme.custom.text};
  text-align: center;
`;

export const TopThreePoints = styled.div<{ theme?: CustomTheme }>`
  font-size: 11px;
  color: ${({ theme }) => theme.custom.textLight};
  font-weight: 500;
  text-align: center;
`;

export const RulesContainer = styled(Box)<{ theme?: CustomTheme }>`
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  background-color: ${({ theme }) => theme.custom.background3};
`;

export const RulesContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const RulesIconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RewardsContainer = styled.div`
  margin-top: 24px;
`;

export const RewardsTitle = styled.div<{ theme?: CustomTheme }>`
  font-weight: 500;
  font-size: 18px;
  color: ${({ theme }) => theme.custom.text};
  margin-bottom: 16px;
`;

export const RewardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
`;

export const StandingsContainer = styled.div`
  margin-top: 8px;
`;

export const StandingsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const StandingsTitle = styled.div<{ theme?: CustomTheme }>`
  font-weight: 500;
  font-size: 18px;
  color: ${({ theme }) => theme.custom.text};
`;

export const ManagePointsButton = styled.div<{ theme?: CustomTheme }>`
  color: ${({ theme }) => theme.custom.primary};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
`;

export const TableHeaderContainer = styled.div<{ theme?: CustomTheme }>`
  background-color: ${({ theme }) => theme.custom.primary};
  padding: 12px 24px;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 10% 60% 30%;
  align-items: center;
  margin-bottom: 8px;
`;

export const TableHeader = styled.div`
  color: white;
  font-weight: 700;
  font-size: 14px;
  &:last-child {
    text-align: right;
  }
`;

export const TableRow = styled.div<{
  isEven: boolean;
  isNested?: boolean;
  theme?: CustomTheme;
}>`
  display: grid;
  grid-template-columns: 10% 60% 30%;
  align-items: center;
  padding: 12px ${({ isNested }) => (isNested ? '40px' : '24px')};
  background-color: ${({ isEven, theme }) =>
    isEven ? theme.custom.background2 : theme.custom.background1};
  border-radius: 8px;
  margin-bottom: 4px;
`;

export const IndentedContent = styled.div``;

export const TableCell = styled.div<{ theme?: CustomTheme }>`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.custom.text};
  &:last-child {
    justify-content: flex-end;
  }
`;

export const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ExpandButton = styled.div`
  margin-left: auto;
`;

export const PlayerAvatar = styled(Avatar)<{ theme?: CustomTheme }>`
  width: 32px;
  height: 32px;
  background-color: ${({ theme }) => theme.custom.background3};
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.custom.textLight};
  text-transform: uppercase;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;

export const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: 12px;
  & svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

export const SearchInput = styled('input')<{ theme?: CustomTheme }>`
  padding: 8px 12px 8px 48px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 14px;
  width: 100%;
  outline: none;
  height: 48px;
  background-color: ${({ theme }) => theme.custom.background2};
  color: ${({ theme }) => theme.custom.text};

  &:focus {
    border-color: ${({ theme }) => theme.custom.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.custom.textLight};
  }
`;

export const KpiContainer = styled.div`
  padding-left: 48px;
`;

export const KpiRow = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  color: ${({ theme }) => theme.custom.text};

  &:last-child {
    border-bottom: none;
  }
`;

export const KpiText = styled.span<{ theme?: CustomTheme }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.custom.text};
`;

export const KpiPointsText = styled.span<{ theme?: CustomTheme }>`
  color: ${({ theme }) => theme.custom.textLight};
`;

export const RewardCardContainer = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 16/9;
`;

export const RewardCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const RewardCardDarkOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
`;

export const RewardCardDetailsContainer = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const RewardCardPlace = styled.div`
  color: #ff5722;
  font-weight: 700;
  font-size: 14px;
`;

export const RewardCardName = styled.div`
  color: white;
  font-weight: 600;
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PointsInput = styled(TextField)`
  width: 80px;
  .MuiInputBase-root {
    background-color: #d3d3d3;
    border-radius: 8px;
    &:before,
    &:after {
      display: none;
    }
  }
  .MuiInputBase-input {
    font-weight: 700;
    padding: 8px;
    font-size: 14px;
    text-align: center;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type='number'] {
      -moz-appearance: textfield;
    }
  }
  .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`;
